import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bookmark, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface SavedResource {
  id: string;
  notes: string | null;
  created_at: string;
  resource: {
    id: string;
    title: string;
    description: string;
    slug: string | null;
    type: string;
    thumbnail_url: string | null;
  };
}

const SavedResources: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: savedResources, isLoading } = useQuery({
    queryKey: ["saved-resources"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_saved_resources")
        .select(`
          id,
          notes,
          created_at,
          resource:library_items(id, title, description, slug, type, thumbnail_url)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as SavedResource[];
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("user_saved_resources")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-resources"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Resource removed from saved");
    },
    onError: () => {
      toast.error("Failed to remove resource");
    },
  });

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
          <Bookmark className="h-8 w-8 text-primary" />
          Saved Resources
        </h1>
        <p className="text-muted-foreground mt-2">
          Resources you've bookmarked from the library.
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : !savedResources?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bookmark className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No saved resources yet.</p>
            <Link to="/library">
              <Button variant="outline" className="mt-4">
                Browse Library
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedResources.map((saved) => (
            <Card key={saved.id} className="group">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg line-clamp-2">
                    {saved.resource?.title || "Unknown Resource"}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                    onClick={() => removeMutation.mutate(saved.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  {saved.resource?.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground capitalize">
                    {saved.resource?.type}
                  </span>
                  {saved.resource?.slug && (
                    <Link to={`/library/${saved.resource.slug}`}>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedResources;
